import { Injectable, Optional, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { GameType } from 'src/app/services/ConfigurationManagerService';
import { StepperChangeEvent, StepperConfig, StepperStatus, StepperStepDefinition } from './stepper.types';

const GAME_TYPE_CONTENT_LABELS: Record<GameType, string> = {
  [GameType.CODEHUNT]: 'QR Codes',
  [GameType.MULTI_GAMES]: 'Jeux',
  [GameType.MULTI_QUIZ]: 'Quiz',
  [GameType.TREASURE_HUNT]: 'Étapes',
  [GameType.RIDDLE_HUNT]: 'Énigmes',
  [GameType.LANDING_PAGE]: 'Liste des jeux',
};

/**
 * Libellé complet et personnalisé du step de contenu, par type de jeu.
 * Prioritaire sur le libellé générique `Édition de ${contentLabel}` lorsqu'il est
 * défini : permet un intitulé grammaticalement correct (ex. « Édition des étapes »).
 */
const GAME_TYPE_CONTENT_FULL_LABELS: Partial<Record<GameType, string>> = {
  [GameType.CODEHUNT]: 'Édition des QR Codes',
  [GameType.MULTI_GAMES]: 'Édition des jeux',
  [GameType.TREASURE_HUNT]: 'Édition des étapes',
  [GameType.RIDDLE_HUNT]: 'Édition des énigmes',
};

const GAME_TYPE_CONTENT_KEYS: Record<GameType, string> = {
  [GameType.CODEHUNT]: 'content-codehunt',
  [GameType.MULTI_GAMES]: 'content-multigames',
  [GameType.MULTI_QUIZ]: 'content-multiquiz',
  [GameType.TREASURE_HUNT]: 'content-treasurehunt',
  [GameType.RIDDLE_HUNT]: 'content-riddlehunt',
  [GameType.LANDING_PAGE]: 'content-landingpage',
};

const GAME_TYPE_STRUCTURE_KEYS: Partial<Record<GameType, string>> = {
  [GameType.RIDDLE_HUNT]: 'structure-riddlehunt',
  [GameType.LANDING_PAGE]: 'structure-landingpage',
};

const LANGUAGE_LABELS: Record<string, string> = {
  fr: 'Français',
  gb: 'Anglais',
  en: 'Anglais',
  de: 'Allemand',
  es: 'Espagnol',
  it: 'Italien',
  pt: 'Portugais',
  nl: 'Néerlandais',
  pl: 'Polonais',
  ar: 'Arabe',
  zh: 'Chinois',
  ja: 'Japonais',
};

@Injectable({
  providedIn: 'root'
})
export class StepperService {
  private readonly _config = signal<StepperConfig | null>(null);
  private readonly _currentStepKey = signal<string | null>(null);
  private readonly _manualErrors = signal<Record<string, boolean>>({});
  private readonly _isValidated = signal(false);
  private readonly _revision = signal(0);

  /**
   * Route courante suivie via les évènements NavigationEnd. Sert de garde
   * anti-écrasement : une page quittée ne doit pas pouvoir écraser le stepper
   * de la page affichée en terminant son chargement asynchrone en retard.
   */
  private readonly _currentRoute = signal('');

  private readonly _changeSubject = new Subject<StepperChangeEvent>();

  public readonly change$: Observable<StepperChangeEvent> = this._changeSubject.asObservable();
  public readonly config = this._config.asReadonly();
  public readonly currentStepKey = this._currentStepKey.asReadonly();

  public readonly steps = computed<StepperStepDefinition[]>(() => {
    this._revision();
    return this._config()?.steps ?? [];
  });

  public readonly currentStepIndex = computed<number>(() => {
    const currentStepKey = this._currentStepKey();
    if (!currentStepKey) {
      return -1;
    }

    return this.steps().findIndex(step => step.key === currentStepKey);
  });

  public readonly statusByKey = computed<Record<string, StepperStatus>>(() => {
    this._revision();

    const currentStepIndex = this.currentStepIndex();
    const steps = this.steps();
    const manualErrors = this._manualErrors();
    const isValidated = this._isValidated();
    const statuses: Record<string, StepperStatus> = {};

    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      const step = steps[stepIndex];

      if (isValidated) {
        statuses[step.key] = 'validated';
        continue;
      }

      if (stepIndex === currentStepIndex) {
        statuses[step.key] = 'current';
        continue;
      }

      if (step.permanentStatus) {
        statuses[step.key] = step.permanentStatus;
        continue;
      }

      const hasError = manualErrors[step.key] ?? (step.isValid ? !step.isValid() : false);
      if (hasError) {
        statuses[step.key] = 'error';
        continue;
      }

      statuses[step.key] = stepIndex < currentStepIndex ? 'completed' : 'waiting';
    }

    return statuses;
  });

  public readonly canGoPrevious = computed<boolean>(() => this.currentStepIndex() > 0);

  public readonly canGoNext = computed<boolean>(() => {
    const steps = this.steps();
    const currentStepIndex = this.currentStepIndex();
    return currentStepIndex >= 0 && currentStepIndex < steps.length - 1;
  });

  public readonly viewModel = computed(() => {
    const steps = this.steps();
    const statuses = this.statusByKey();
    const currentStepKey = this._currentStepKey();

    return steps.map((step, index) => ({
      ...step,
      index,
      status: statuses[step.key] ?? 'waiting',
      isCurrent: step.key === currentStepKey,
      isClickable: this.canAccessStep(step, index),
    }));
  });

  constructor(@Optional() private readonly router: Router | null = null) {
    // Suivi fiable de la route courante. IMPORTANT : on s'appuie sur l'URL de
    // l'évènement NavigationEnd et NON sur `router.url`, qui peut rester périmé
    // avec la navigation Ionic (`navigateRoot`).
    if (this.router) {
      this._currentRoute.set(this.normalizeStepperUrl(this.router.url));
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this._currentRoute.set(this.normalizeStepperUrl(event.urlAfterRedirects || event.url));
        }
      });
    }
  }

  private normalizeStepperUrl(url: string): string {
    return (url || '').split('?')[0].split('#')[0];
  }

  /**
   * Vrai si la route courante appartient à l'un des `routePrefixes`. Si la route
   * est inconnue (non initialisée), renvoie true pour ne jamais bloquer à tort
   * (robustesse à l'initialisation et en environnement de test).
   */
  private isActiveRouteWithin(routePrefixes: string[]): boolean {
    const route = this._currentRoute();
    if (!route) return true;
    return routePrefixes.some(prefix => route === prefix || route.startsWith(prefix + '/'));
  }

  public setConfig(config: StepperConfig | null): void {
    if (!config || !Array.isArray(config.steps) || config.steps.length === 0) {
      this.clear();
      return;
    }

    // Garde anti-écrasement inter-pages : une page que l'on vient de quitter peut
    // terminer son chargement asynchrone et appeler setConfig EN RETARD. Si la route
    // courante n'appartient pas aux `routePrefixes` de cette config, on l'ignore pour
    // ne pas écraser — puis masquer — le stepper de la page réellement affichée.
    const guardRoutePrefixes = config.routePrefixes ?? [];
    if (guardRoutePrefixes.length > 0 && !this.isActiveRouteWithin(guardRoutePrefixes)) {
      return;
    }

    this._config.set({
      mode: config.mode ?? 'free',
      showNavigation: config.showNavigation ?? true,
      initialStepKey: config.initialStepKey,
      routePrefixes: [...(config.routePrefixes ?? [])],
      steps: [...config.steps],
    });
    this._manualErrors.set({});
    this._isValidated.set(false);

    const initialStepKey = config.initialStepKey && config.steps.some(step => step.key === config.initialStepKey)
      ? config.initialStepKey
      : config.steps[0].key;

    const previousStepKey = this._currentStepKey();
    this._currentStepKey.set(initialStepKey);
    this.bumpRevision();
    this.emitChange(previousStepKey, initialStepKey, 'set-config');
  }

  public clear(): void {
    const previousStepKey = this._currentStepKey();
    this._config.set(null);
    this._currentStepKey.set(null);
    this._manualErrors.set({});
    this._isValidated.set(false);
    this.bumpRevision();
    this.emitChange(previousStepKey, null, 'clear');
  }

  public reset(): boolean {
    const steps = this.steps();
    if (steps.length === 0) {
      return false;
    }

    const previousStepKey = this._currentStepKey();
    const nextStepKey = this._config()?.initialStepKey && steps.some(step => step.key === this._config()?.initialStepKey)
      ? this._config()?.initialStepKey ?? steps[0].key
      : steps[0].key;

    this._currentStepKey.set(nextStepKey);
    this.bumpRevision();
    this.emitChange(previousStepKey, nextStepKey, 'reset');
    return true;
  }

  public goTo(key: string, options: { force?: boolean } = {}): boolean {
    const steps = this.steps();
    const targetIndex = steps.findIndex(step => step.key === key);
    if (targetIndex < 0) {
      return false;
    }

    const targetStep = steps[targetIndex];
    if (!options.force && !this.canAccessStep(targetStep, targetIndex)) {
      return false;
    }

    const previousStepKey = this._currentStepKey();
    this._currentStepKey.set(targetStep.key);
    this.bumpRevision();
    this.emitChange(previousStepKey, targetStep.key, 'goto');
    return true;
  }

  public next(): boolean {
    const steps = this.steps();
    const currentStepIndex = this.currentStepIndex();
    if (currentStepIndex < 0 || currentStepIndex >= steps.length - 1) {
      return false;
    }

    const nextStep = steps[currentStepIndex + 1];
    if (!this.canAccessStep(nextStep, currentStepIndex + 1)) {
      return false;
    }

    if (nextStep.action) {
      nextStep.action();
      return true;
    }

    const previousStepKey = this._currentStepKey();
    this._currentStepKey.set(nextStep.key);
    this.bumpRevision();
    this.emitChange(previousStepKey, nextStep.key, 'next');
    return true;
  }

  public previous(): boolean {
    const steps = this.steps();
    const currentStepIndex = this.currentStepIndex();
    if (currentStepIndex <= 0) {
      return false;
    }

    const previousStep = steps[currentStepIndex - 1];
    if (previousStep.action) {
      previousStep.action();
      return true;
    }

    const previousStepKey = this._currentStepKey();
    const nextStepKey = previousStep.key;
    this._currentStepKey.set(nextStepKey);
    this.bumpRevision();
    this.emitChange(previousStepKey, nextStepKey, 'previous');
    return true;
  }

  public markValidated(): void {
    this._isValidated.set(true);
    this.bumpRevision();
  }

  public setStepError(stepKey: string, hasError: boolean): void {
    this._manualErrors.update(errors => {
      if (!hasError && !Object.prototype.hasOwnProperty.call(errors, stepKey)) {
        return errors;
      }

      const nextErrors = { ...errors };
      if (hasError) {
        nextErrors[stepKey] = true;
      } else {
        delete nextErrors[stepKey];
      }
      return nextErrors;
    });

    this.bumpRevision();
  }

  public notifyStateChanged(): void {
    this.bumpRevision();
  }

  public buildStepsForGameType(gameType: GameType, languages: string[]): StepperStepDefinition[] {
    if (!languages || languages.length === 0) {
      return [];
    }

    const contentKey = GAME_TYPE_CONTENT_KEYS[gameType] || 'content';
    const contentLabel = GAME_TYPE_CONTENT_LABELS[gameType] || 'Contenu';
    const contentFullLabel = GAME_TYPE_CONTENT_FULL_LABELS[gameType] || `Édition de ${contentLabel}`;
    const structureKey = GAME_TYPE_STRUCTURE_KEYS[gameType] || 'structure';

    const steps: StepperStepDefinition[] = [
      {
        key: structureKey,
        label: 'Structure',
      },
    ];

    if (languages.length === 1) {
      steps.push({
        key: contentKey,
        label: contentFullLabel,
        language: languages[0],
      });

      return steps;
    }

    const primaryLanguage = languages[0];
    steps.push({
      key: `${contentKey}-${primaryLanguage}`,
      label: contentFullLabel,
      language: primaryLanguage,
    });

    steps.push({
      key: 'multilang-structure',
      label: 'Structure multilingue',
    });

    for (let languageIndex = 1; languageIndex < languages.length; languageIndex++) {
      const language = languages[languageIndex];
      steps.push({
        key: `${contentKey}-${language}`,
        label: `Édition en ${this.getLanguageLabel(language)}`,
        language,
      });
    }

    return steps;
  }

  private canAccessStep(step: StepperStepDefinition, stepIndex: number): boolean {
    if (step.isDisabled) {
      return false;
    }

    const config = this._config();
    const mode = config?.mode ?? 'free';
    if (mode === 'free') {
      return step.canEnter ? step.canEnter() : true;
    }

    const currentStepIndex = this.currentStepIndex();
    if (stepIndex <= currentStepIndex) {
      return true;
    }

    if (step.canEnter) {
      return step.canEnter();
    }

    return stepIndex === currentStepIndex + 1;
  }

  private emitChange(previousStepKey: string | null, currentStepKey: string | null, action: StepperChangeEvent['action']): void {
    const steps = this.steps();
    const currentStepIndex = currentStepKey ? steps.findIndex(step => step.key === currentStepKey) : -1;
    const previousStepIndex = previousStepKey ? steps.findIndex(step => step.key === previousStepKey) : -1;
    const step = currentStepIndex >= 0 ? steps[currentStepIndex] : null;

    this._changeSubject.next({
      previousStepKey,
      currentStepKey,
      previousStepIndex,
      currentStepIndex,
      step,
      action,
      language: step?.language,
      timestamp: Date.now(),
    });
  }

  private bumpRevision(): void {
    this._revision.update(value => value + 1);
  }

  private getLanguageLabel(languageCode: string): string {
    return LANGUAGE_LABELS[languageCode] || languageCode.toUpperCase();
  }
}
